/**
 * The client's half of `operating_hours` — the shape, its three rules, and the one-line summary
 * (POS2-1210).
 *
 * `app/schemas/operating_hours.py` is the contract and this mirrors it deliberately rather than
 * loosely: all seven days present, `closes_at` strictly after `opens_at`, no overlapping windows
 * in a day. A value this module accepts is a value the resolver accepts, which is the only reason
 * the editor can be trusted to write straight into `jsonb` that has no CHECK behind it.
 *
 * The two things it does **not** mirror, both on purpose:
 *
 * - **`{}` is not an `OperatingHours` here either, and this module never produces one.** The unset
 *   state is `null` in the form and `{}` on the wire; keeping it out of the type means every
 *   function below takes a whole week and none of them has an "or nothing" branch.
 * - **Overnight service is rejected, not wrapped.** A window past midnight is written as the two
 *   civil-day windows it spans, because every period in this product is a civil day. So
 *   `closes_at <= opens_at` is a mistake to report, never a wrap to interpret.
 *
 * Problems come back as codes rather than messages so this stays free of `t` — the component maps
 * a code to a literal key, which is what `check:i18n` can see.
 */

import { z } from "zod";

import type { Locale } from "@/i18n/config";
import type { TFunction } from "@/i18n/catalog";
import { weekdayLabels } from "@/lib/format";

export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

export interface ServiceWindow {
  opens_at: string;
  closes_at: string;
}

export type OperatingHours = Record<Weekday, ServiceWindow[]>;

export type DayProblem = "incomplete" | "closesBeforeOpens" | "overlap";

/** `HH:MM`, with the seconds Postgres may have written on the end trimmed off. */
const clockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)
  .transform((s) => s.slice(0, 5));

const serviceWindow = z.object({ opens_at: clockTime, closes_at: clockTime });

/** Structure only. The three rules live in `dayProblems`, which the editor needs per day. */
const weekShape = z.object({
  monday: z.array(serviceWindow),
  tuesday: z.array(serviceWindow),
  wednesday: z.array(serviceWindow),
  thursday: z.array(serviceWindow),
  friday: z.array(serviceWindow),
  saturday: z.array(serviceWindow),
  sunday: z.array(serviceWindow),
});

/** The first thing wrong with each day, keyed by day. Empty means the week is storable. */
export function dayProblems(hours: OperatingHours): Partial<Record<Weekday, DayProblem>> {
  const problems: Partial<Record<Weekday, DayProblem>> = {};
  for (const day of WEEKDAYS) {
    const windows = hours[day];
    if (windows.some((w) => !w.opens_at || !w.closes_at)) {
      problems[day] = "incomplete";
    } else if (windows.some((w) => w.closes_at <= w.opens_at)) {
      problems[day] = "closesBeforeOpens";
    } else if (overlaps(windows)) {
      problems[day] = "overlap";
    }
  }
  return problems;
}

function overlaps(windows: ServiceWindow[]): boolean {
  const sorted = [...windows].sort((a, b) => a.opens_at.localeCompare(b.opens_at));
  return sorted.some((later, i) => i > 0 && later.opens_at < sorted[i - 1].closes_at);
}

export function isStorable(hours: OperatingHours): boolean {
  return Object.keys(dayProblems(hours)).length === 0;
}

/**
 * A stored or resolved value as a week, or `null` when it is not one.
 *
 * `null` covers the two cases the editor treats identically and correctly: `{}`, which is what
 * every organisation holds and means nobody has configured hours, and a value the resolver already
 * rejected — the screen shows the resolved default and says which level was rejected, rather than
 * loading a broken week into the editor for someone to puzzle over.
 */
export function parseWeek(value: unknown): OperatingHours | null {
  const parsed = weekShape.safeParse(value);
  if (!parsed.success) return null;
  return isStorable(parsed.data) ? parsed.data : null;
}

/** The shape of the platform default's service day, for a week that is closed everywhere. */
const DEFAULT_SERVICE: ServiceWindow = { opens_at: "07:00", closes_at: "15:00" };

/**
 * The window the add button proposes.
 *
 * Two different acts share one button and they want different answers. Adding to a day that
 * already serves is a *second* service: it starts where the day currently ends and runs two hours,
 * so the ordinary dinner-after-lunch case lands valid without being edited.
 *
 * Re-opening a *closed* day is not that. There is no end to start from, and a two-hour stub is a
 * guess about a kitchen whose own week is sitting right there on the screen — so it copies the
 * first service the rest of the week runs, falling back to the inherited week and then to the
 * platform default's shape for a week that is closed everywhere. One window either way: the button
 * says it adds a service window, and copying a split day's two would make it a liar.
 */
export function addedWindow(
  hours: OperatingHours,
  day: Weekday,
  inherited: OperatingHours | null,
): ServiceWindow {
  const windows = hours[day];
  if (windows.length) {
    const opens = windows[windows.length - 1].closes_at;
    return { opens_at: opens, closes_at: plusTwoHours(opens) };
  }
  return firstService(hours) ?? firstService(inherited) ?? DEFAULT_SERVICE;
}

function firstService(hours: OperatingHours | null): ServiceWindow | null {
  if (!hours) return null;
  for (const day of WEEKDAYS) {
    if (hours[day].length) return hours[day][0];
  }
  return null;
}

function plusTwoHours(time: string): string {
  const [h, m] = time.split(":").map(Number);
  return h + 2 >= 24 ? "23:59" : `${String(h + 2).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * The week on one line: `Mon–Fri 07:00–15:00 · Sat–Sun Closed`.
 *
 * Runs of consecutive days sharing the same windows collapse, because the shape of a service week
 * is what the reader is checking and seven rows of mostly-identical text hides it. Weekday names
 * come from `Intl` via `weekdayLabels` for the reason POS2-1176 gave — a weekday name is
 * formatting, not copy.
 */
export function summariseWeek(hours: OperatingHours, locale: Locale, t: TFunction): string {
  const labels = weekdayLabels(locale);
  const parts: string[] = [];
  let runStart = 0;

  for (let i = 0; i < WEEKDAYS.length; i++) {
    const next = WEEKDAYS[i + 1];
    if (next && sameWindows(hours[WEEKDAYS[i]], hours[next])) continue;
    const days = runStart === i ? labels[i] : `${labels[runStart]}–${labels[i]}`;
    parts.push(`${days} ${describeDay(hours[WEEKDAYS[i]], t)}`);
    runStart = i + 1;
  }

  return parts.join(" · ");
}

function describeDay(windows: ServiceWindow[], t: TFunction): string {
  if (windows.length === 0) return t("settings.serviceHoursClosed");
  return windows.map((w) => `${w.opens_at}–${w.closes_at}`).join(", ");
}

function sameWindows(a: ServiceWindow[], b: ServiceWindow[]): boolean {
  return (
    a.length === b.length &&
    a.every((w, i) => w.opens_at === b[i].opens_at && w.closes_at === b[i].closes_at)
  );
}
